import React, { useMemo } from "react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  CUSTOM_LABEL,
  CUSTOM_PATH,
  groupRoutes,
  pathKey,
  uniqueLabelRoutes,
} from "@/lib/publicRoutes";

const inputClass =
  "bg-black/25 border-white/10 text-white placeholder:text-white/25 focus-visible:ring-cyan-400/30";

/**
 * Fully dynamic label + endpoint pickers backed by GET /navigation/routes.
 * `value` shape: { label, href } (footer) or { label, url } (navigation).
 */
export default function PublicRouteLinkFields({
  value = {},
  onChange,
  hrefKey = "href",
  compact = false,
  className = "",
  routes = [],
}) {
  const label = value.label || "";
  const href = value[hrefKey] || "";
  const knownPaths = useMemo(() => new Set((routes || []).map((r) => r.path)), [routes]);
  const labelOptions = useMemo(() => uniqueLabelRoutes(routes), [routes]);
  const labelGrouped = useMemo(() => groupRoutes(labelOptions), [labelOptions]);
  const pathGrouped = useMemo(() => groupRoutes(routes), [routes]);

  const pathKnown = knownPaths.has(pathKey(href));
  const labelKnown = labelOptions.some((r) => r.label === label);
  const pathSelectValue = pathKnown ? pathKey(href) : CUSTOM_PATH;
  const labelSelectValue = labelKnown ? label : CUSTOM_LABEL;
  const isExternal = /^https:\/\//i.test(href);
  const triggerClass = compact
    ? `${inputClass} text-xs h-7`
    : `${inputClass} text-xs h-8`;

  const patch = (next) => onChange({ ...value, ...next });

  const pickByLabel = (labelKey) => {
    if (labelKey === CUSTOM_LABEL) {
      patch({ label: "" });
      return;
    }
    const match = (routes || []).find((r) => r.label === labelKey);
    if (!match) {
      patch({ label: labelKey });
      return;
    }
    patch({ label: match.label, [hrefKey]: match.path });
  };

  const pickByPath = (path) => {
    if (path === CUSTOM_PATH) {
      patch({ [hrefKey]: "" });
      return;
    }
    const match = (routes || []).find((r) => r.path === path);
    patch({
      [hrefKey]: path,
      label: match?.label || label || path,
    });
  };

  return (
    <div className={`space-y-1.5 ${className}`}>
      <div className={compact ? "grid grid-cols-1 gap-1" : "grid grid-cols-1 sm:grid-cols-2 gap-1.5"}>
        <div className="space-y-1 min-w-0">
          {!compact && <p className="text-[10px] uppercase tracking-wider text-white/40">Link name</p>}
          <Select value={labelSelectValue} onValueChange={pickByLabel}>
            <SelectTrigger className={triggerClass}>
              <SelectValue placeholder="Choose name" />
            </SelectTrigger>
            <SelectContent className="glass-strong border-white/10 text-white max-h-72">
              {labelGrouped.map(({ group, items }) => (
                <SelectGroup key={`lbl-${group}`}>
                  <SelectLabel className="text-white/45 text-[10px] uppercase tracking-wider">{group}</SelectLabel>
                  {items.map((r) => (
                    <SelectItem key={`lbl-${r.path}`} value={r.label} className="text-white focus:bg-white/10 focus:text-white">
                      {r.label}
                    </SelectItem>
                  ))}
                </SelectGroup>
              ))}
              <SelectGroup>
                <SelectLabel className="text-white/45 text-[10px] uppercase tracking-wider">Custom</SelectLabel>
                <SelectItem value={CUSTOM_LABEL} className="text-white focus:bg-white/10 focus:text-white">
                  Custom name…
                </SelectItem>
              </SelectGroup>
            </SelectContent>
          </Select>
          {labelSelectValue === CUSTOM_LABEL && (
            <Input
              value={label}
              onChange={(e) => patch({ label: e.target.value })}
              className={triggerClass}
              placeholder="Custom label"
            />
          )}
        </div>

        <div className="space-y-1 min-w-0">
          {!compact && <p className="text-[10px] uppercase tracking-wider text-white/40">Endpoint</p>}
          <Select value={pathSelectValue} onValueChange={pickByPath}>
            <SelectTrigger className={triggerClass}>
              <SelectValue placeholder="Choose /path" />
            </SelectTrigger>
            <SelectContent className="glass-strong border-white/10 text-white max-h-72">
              {pathGrouped.map(({ group, items }) => (
                <SelectGroup key={`path-${group}`}>
                  <SelectLabel className="text-white/45 text-[10px] uppercase tracking-wider">{group}</SelectLabel>
                  {items.map((r) => (
                    <SelectItem key={`path-${r.path}`} value={r.path} className="text-white focus:bg-white/10 focus:text-white">
                      {`${r.path} — ${r.label}`}
                    </SelectItem>
                  ))}
                </SelectGroup>
              ))}
              <SelectGroup>
                <SelectLabel className="text-white/45 text-[10px] uppercase tracking-wider">Custom</SelectLabel>
                <SelectItem value={CUSTOM_PATH} className="text-white focus:bg-white/10 focus:text-white">
                  Custom / or https://…
                </SelectItem>
              </SelectGroup>
            </SelectContent>
          </Select>
          {(pathSelectValue === CUSTOM_PATH || isExternal) && (
            <Input
              value={href}
              onChange={(e) => patch({ [hrefKey]: e.target.value })}
              className={triggerClass}
              placeholder="/path or https://…"
            />
          )}
        </div>
      </div>
    </div>
  );
}
